<template>
  <div>
    <div class="card card-extended mb-3" v-if="showContractPanel">
      <div class="card-header">
        <section-title
          icon="fas fa-plus text-orange"
          content="Información de Contrato de Usuario"
          class="custom-section-title"
        />
      </div>
      <div class="card-body">
        <validation-observer ref="sipaeProfile">
          <div class="form-row">
            <div>
              <validation-provider
                :rules="{
                  required: model.contracts.length === 0 || dirty,
                }"
                v-slot="{ errors }"
              >
                <custom-input
                  v-model="currentModel.contractNumber"
                  label="Número de Contrato:"
                  :error="errors[0]"
                  custom-error-msg="Favor escriba el número de contrato."
                />
              </validation-provider>
            </div>
            <div>
              <validation-provider
                :rules="{
                  required: model.contracts.length === 0 || dirty,
                }"
                v-slot="{ errors }"
              >
                <custom-date-picker
                  v-model="currentModel.signedDate"
                  label="Fecha de Firma de Contrato:"
                  :error="errors[0]"
                  :custom-error-msg="'Favor seleccione la fecha de firma.'"
                  class-container="w-100"
                />
              </validation-provider>
            </div>
            <div>
              <validation-provider
                :rules="{
                  required: model.contracts.length === 0 || dirty,
                }"
                v-slot="{ errors }"
              >
                <custom-date-picker
                  v-model="currentModel.fromDate"
                  label="Fecha Desde:"
                  :error="errors[0]"
                  :custom-error-msg="'Favor seleccione la fecha de desde.'"
                  class-container="w-100"
                />
              </validation-provider>
            </div>
            <div>
              <validation-provider
                :rules="{
                  dateGreaterThan: currentModel.fromDate,
                  required: model.contracts.length === 0 || dirty,
                }"
                v-slot="{ errors, failedRules }"
              >
                <custom-date-picker
                  v-model="currentModel.toDate"
                  label="Fecha Hasta:"
                  :error="errors[0]"
                  :custom-error-msg="
                    failedRules.dateGreaterThan
                      ? 'La fecha debe ser mayor a la fecha desde.'
                      : 'Favor seleccione la fecha de hasta.'
                  "
                  class-container="w-100"
                />
              </validation-provider>
            </div>
            <div class="mt-4">
              <action-button
                v-if="!isEditMode"
                variant="success"
                class="w-fit mt-2"
                text="Añadir"
                i="fas fa-file-medical"
                @click="handleAddContract"
              />
              <action-button
                v-if="isEditMode"
                variant="success"
                class="w-fit mt-2 ml-2"
                text="Guardar"
                i="fas fa-save"
                @click="save"
              />
              <action-button
                v-if="isEditMode"
                variant="danger"
                class="w-fit mt-2 ml-2"
                text="Cancelar"
                i="fas fa-times-circle"
                @click="
                  () => {
                    resetModel();
                    isEditMode = false;
                  }
                "
              />
            </div>
          </div>
        </validation-observer>

        <div class="mt-4">
          <custom-callout
            v-for="(contract, index) in model.contracts"
            :key="`contract-${index}`"
            :has-border="false"
            :has-toggle="false"
            class="callout-extended mb-3"
          >
            <div class="row my-0">
              <div class="col-md-10">
                <div class="row mt-2">
                  <div class="col-md-3">
                    <div class="color-secondary-6 font-16">
                      Número de Contrato:
                    </div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ contract.contractNumber }}
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="color-secondary-6 font-16">Fecha de Firma:</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ contract.signedDate | date }}
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="color-secondary-6 font-16">Fecha Desde:</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ contract.fromDate | date }}
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="color-secondary-6 font-16">Fecha Hasta:</div>
                    <div class="color-black font-16 font-weight-normal">
                      {{ contract.toDate | date }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="col-md-2 d-flex justify-content-end align-items-center pr-5"
              >
                <action-button
                  v-if="canRemove(contract)"
                  variant="outline-danger"
                  i="fas fa-times"
                  class="mr-2"
                  @click="handleRemoveContract(contract, index)"
                />
                <action-button
                  v-if="canEdit(contract)"
                  variant="success"
                  i="fas fa-edit"
                  @click="triggerEditMode(index, contract)"
                />
              </div>
            </div>
          </custom-callout>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import CustomDatePicker from 'custom-date-picker';
import ActionButton from 'ActionButton';
import CustomCallout from 'custom-callout';
import { extend } from 'vee-validate';
import rolesIds from '@/utils/constants/rolesIds';
import alertMixin from '@/mixins/alert.mixin';

extend('dateGreaterThan', {
  validate(value, { other }) {
    return new Date(value) > new Date(other);
  },
  params: ['other'],
  message: 'La fecha debe ser mayor a {other}.',
});

export default {
  name: 'SipaeeProfileContract',
  components: {
    SectionTitle,
    CustomInput,
    CustomDatePicker,
    ActionButton,
    CustomCallout,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      currentIndex: null,
      isEditMode: false,
      currentModel: {
        id: null,
        contractNumber: '',
        signedDate: '',
        fromDate: '',
        toDate: '',
      },
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    showContractPanel() {
      const validRoles = [
        rolesIds.JuezAdministrativo,
        rolesIds.JuezCoordinador,
        rolesIds.Mediator,
      ];
      const userRoles = this.user.roles.map((role) =>
        role.roleId.toUpperCase()
      );
      const isValid = userRoles.some((role) => validRoles.includes(role));
      return isValid;
    },
    dirty() {
      return Object.values(this.currentModel).some((value) => value);
    },
    canRemove() {
      return (item) => {
        if (!item.id) return true;
        const hasPermission = this.userHasPermissions(
          'security.sipaeeuser.contract.delete'
        );
        if (!hasPermission) return false;
        return this.isContractValid(item);
      };
    },
    canEdit() {
      return (item) => {
        const hasPermission = this.userHasPermissions(
          'security.sipaeeuser.contract.edit'
        );
        if (!hasPermission) return false;
        return this.isContractValid(item);
      };
    },
    isContractValid() {
      return (item) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inRange = today >= item.fromDate && today <= item.toDate;
        return inRange;
      }
    },
  },
  async mounted() {
    //await this.getStudents();
  },
  methods: {
    handleAddContract() {
      this.$refs.sipaeProfile.validate().then((success) => {
        if (success) {
          if (
            Object.keys(this.currentModel).some(
              (key) => key !== 'id' && !this.currentModel[key]
            )
          ) {
            return;
          }
          this.model.contracts.push(this.currentModel);
          this.currentModel = {
            contractNumber: '',
            signedDate: '',
            fromDate: '',
            toDate: '',
          };
          this.$refs.sipaeProfile.reset();
        }
      });
    },
    async handleRemoveContract(contract, index) {
      this.$emit('remove', contract, index);
    },
    async triggerEditMode(index, contract) {
      this.currentIndex = index;
      this.isEditMode = true;
      this.currentModel = {
        id: contract.id,
        contractNumber: contract.contractNumber,
        signedDate: contract.signedDate,
        fromDate: contract.fromDate,
        toDate: contract.toDate,
      };
    },
    async resetModel() {
      this.currentModel = {
        id: null,
        contractNumber: '',
        signedDate: '',
        fromDate: '',
        toDate: '',
      };
    },
    async save() {
      const confirmed = await this.ShowConfirm(
        'Confirmación',
        '¿Está seguro que desea guardar la información del contrato?'
      );

      if (!confirmed) return;

      if (!this.currentModel.id) {
        this.model.contracts[this.currentIndex].contractNumber =
          this.currentModel.contractNumber;
        this.model.contracts[this.currentIndex].signedDate =
          this.currentModel.signedDate;
        this.model.contracts[this.currentIndex].fromDate =
          this.currentModel.fromDate;
        this.model.contracts[this.currentIndex].toDate =
          this.currentModel.toDate;
      } else {
        this.$emit('update-contract', this.currentModel);
      }
      this.currentIndex = null;
      this.isEditMode = false;
      this.resetModel();
      this.$refs.sipaeProfile.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/config/_colors.scss';

.w-fit {
  width: fit-content;
}

::v-deep .custom-section-title {
  margin-bottom: 0px !important;

  .tag-badge-info {
    background: linear-gradient(90deg, #ffffff 24.5%, #e8e8e8 90.6%) !important;
  }

  .dot {
    background-color: white !important;
  }
}

::v-deep .callout-extended {
  & > div {
    background-color: white !important;
  }
}

.card-extended {
  background-color: #f7f7f7;
  border: unset;

  & .card-header {
    background-color: #f7f7f7;
    border-bottom: 1px solid #e2e2e2;
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr) fit-content(100%);
  grid-template-rows: 1fr;
  grid-column-gap: 27px;
  grid-row-gap: 0px;
}

@media (max-width: 1200px) {
  .form-row {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .form-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .w-fit {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .w-fit {
    width: 100%;
  }
}
</style>
